import React, { useCallback } from "react";
import { Select, Tabs, TabsProps } from "antd";
import WidgetDrawer from "./WidgetDrawer";
import {
    TrendsComparisionWidget,
    SatisfactionDriversTableWidget,
} from "@components/widgets";
import { find } from "underscore";
import { startCase } from "@ts/utils";
import {
    ISatisfactionDriverData,
    ISatisfactionDriversWidgetProps,
} from "@ts/common-types";

const SatisfactionDriversWidget: React.FC<ISatisfactionDriversWidgetProps> = ({
    title,
    show,
    setShow,
    driverData,
    widgetData,
    activeDriversTab,
    setActiveDriversTab,
    selectedDriver,
    onDriversFilterChange,
    driverFilterOptions,
}) => {
    const onClose = () => {
        setShow(false);
        setActiveDriversTab("all");
    };

    const drawerTitle = useCallback(
        (title: string, data: ISatisfactionDriverData) => {
            const filteredData = find(
                activeDriversTab === "all"
                    ? [...data.positives, ...data.negatives]
                    : data[`${activeDriversTab}s`],
                (driver) => driver.name === title
            );

            return (
                <div className="d-flex justify-content-between align-items-center pe-5">
                    <h5>
                        {`${startCase(title)} NPS Score: `}
                        <span className="text-primary">
                            {filteredData?.score || 0}
                        </span>{" "}
                        <span
                            className={
                                filteredData?.change >= 0
                                    ? "text-success"
                                    : "text-danger"
                            }
                        >
                            {filteredData?.change >= 0 ? "↑" : "↓"}{" "}
                            {filteredData?.change}%
                        </span>
                    </h5>
                    <Select
                        value={selectedDriver || [driverFilterOptions[0].value]}
                        options={driverFilterOptions}
                        onChange={onDriversFilterChange}
                        mode="multiple"
                        maxTagCount={1}
                        allowClear
                        style={{ width: "26ch" }}
                        maxTagTextLength={10}
                    />
                </div>
            );
        },
        [activeDriversTab, widgetData, selectedDriver]
    );

    const items: TabsProps["items"] = [
        { label: "All", key: "all" },
        { label: "Positive", key: "positive" },
        { label: "Negative", key: "negative" },
    ];

    return (
        <WidgetDrawer
            title={drawerTitle(title, driverData)}
            width="85vw"
            show={show}
            onClose={onClose}
            bodyStyle={{ backgroundColor: "#FFFFFF" }}
        >
            <TrendsComparisionWidget
                title="All Reviews"
                isTitleRequired={false}
                isCardRequired={false}
                widgetData={widgetData?.chart}
                graphAttributes={{
                    title: "Sentiment score for the Value for Money theme over time",
                    yAxisTitle: "Sentiment Score",
                    colors: ["#28A745"],
                }}
            />
            <Tabs
                className="pa-custom-tabs w-100 mb-2"
                activeKey={activeDriversTab}
                onChange={(key) => setActiveDriversTab(key)}
                items={items}
            />
            <SatisfactionDriversTableWidget widgetData={widgetData?.table} />
        </WidgetDrawer>
    );
};

export default SatisfactionDriversWidget;
